import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  BarChart3, Users, BookOpen, DollarSign, TrendingUp, 
  Calendar, GraduationCap, Award, Loader2, Download,
  UserCheck, Package, MapPin, School, Filter, X, RefreshCw
} from 'lucide-react';

export default function AdminReportsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rawData, setRawData] = useState({});
  
  // Filter states
  const [filters, setFilters] = useState({
    timeRange: 'all',
    courseId: 'all',
    locationId: 'all',
    instructorId: 'all',
    enrollmentStatus: 'all',
    dateFrom: '',
    dateTo: '',
    // Learner-specific filters
    learnerGrade: 'all',
    learnerSchoolDistrict: 'all',
    learnerPartnerSchool: 'all',
  });
  
  // Filter options
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [schoolDistricts, setSchoolDistricts] = useState([]);
  const [partnerSchools, setPartnerSchools] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showLearnerFilters, setShowLearnerFilters] = useState(false);
  const [filteredLearnersList, setFilteredLearnersList] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (rawData.enrollments) {
      calculateStats();
    }
  }, [filters, rawData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, learnersRes, coursesRes, sectionsRes, enrollmentsRes, locationsRes, kitsRes, applicationsRes, partnerSchoolsRes] = await Promise.all([
        api.get('/users/').catch(() => ({ data: [] })),
        api.get('/learners/').catch(() => ({ data: [] })),
        api.get('/courses/').catch(() => ({ data: [] })),
        api.get('/sections/').catch(() => ({ data: [] })),
        api.get('/enrollments/').catch(() => ({ data: [] })),
        api.get('/locations/').catch(() => ({ data: [] })),
        api.get('/kits/').catch(() => ({ data: [] })),
        api.get('/admin/applications/').catch(() => ({ data: [] })),
        api.get('/partner/schools/').catch(() => ({ data: [] })),
      ]);

      const users = usersRes.data?.results || usersRes.data || [];
      const learners = learnersRes.data?.results || learnersRes.data || [];
      const coursesData = coursesRes.data?.results || coursesRes.data || [];
      const sections = sectionsRes.data?.results || sectionsRes.data || [];
      const enrollments = enrollmentsRes.data?.results || enrollmentsRes.data || [];
      const locationsData = locationsRes.data?.results || locationsRes.data || [];
      const kits = kitsRes.data?.results || kitsRes.data || [];
      const applications = applicationsRes.data?.results || applicationsRes.data || [];
      const partnerSchoolsData = partnerSchoolsRes.data?.results || partnerSchoolsRes.data || [];

      // Set filter options
      setCourses(coursesData);
      setLocations(locationsData);
      setInstructors(users.filter(u => u.role === 'instructor'));
      setPartnerSchools(partnerSchoolsData);
      
      // Extract unique school districts from learners
      const uniqueDistricts = [...new Set(learners.map(l => l.district_name).filter(Boolean))];
      setSchoolDistricts(uniqueDistricts);

      // Store raw data for filtering
      setRawData({
        users,
        learners,
        courses: coursesData,
        sections,
        enrollments,
        locations: locationsData,
        kits,
        applications,
        partnerSchools: partnerSchoolsData,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const { users, learners, courses: coursesData, sections, enrollments, locations: locationsData, kits, applications } = rawData;
    
    // Apply filters to enrollments
    let filteredEnrollments = [...enrollments];
    let filteredSections = [...sections];

    // Filter by course
    if (filters.courseId !== 'all') {
      const courseSectionIds = sections
        .filter(s => s.course === filters.courseId || s.course_id === filters.courseId)
        .map(s => s.id);
      filteredEnrollments = filteredEnrollments.filter(e => 
        courseSectionIds.includes(e.section) || courseSectionIds.includes(e.section_id)
      );
      filteredSections = filteredSections.filter(s => 
        s.course === filters.courseId || s.course_id === filters.courseId
      );
    }

    // Filter by location
    if (filters.locationId !== 'all') {
      const locationSectionIds = sections
        .filter(s => s.location === filters.locationId || s.location_id === filters.locationId)
        .map(s => s.id);
      filteredEnrollments = filteredEnrollments.filter(e => 
        locationSectionIds.includes(e.section) || locationSectionIds.includes(e.section_id)
      );
      filteredSections = filteredSections.filter(s => 
        s.location === filters.locationId || s.location_id === filters.locationId
      );
    }

    // Filter by instructor
    if (filters.instructorId !== 'all') {
      const instructorSectionIds = sections
        .filter(s => s.instructor === filters.instructorId || s.instructor_id === filters.instructorId)
        .map(s => s.id);
      filteredEnrollments = filteredEnrollments.filter(e => 
        instructorSectionIds.includes(e.section) || instructorSectionIds.includes(e.section_id)
      );
      filteredSections = filteredSections.filter(s => 
        s.instructor === filters.instructorId || s.instructor_id === filters.instructorId
      );
    }

    // Filter by enrollment status
    if (filters.enrollmentStatus !== 'all') {
      filteredEnrollments = filteredEnrollments.filter(e => e.status === filters.enrollmentStatus);
    }

    // Filter by date range
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredEnrollments = filteredEnrollments.filter(e => 
        new Date(e.created_at) >= fromDate
      );
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59);
      filteredEnrollments = filteredEnrollments.filter(e => 
        new Date(e.created_at) <= toDate
      );
    }

    // Filter by time range preset
    if (filters.timeRange !== 'all') {
      const now = new Date();
      let startDate;
      switch (filters.timeRange) {
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'quarter':
          startDate = new Date(now.setMonth(now.getMonth() - 3));
          break;
        case 'year':
          startDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
      }
      if (startDate) {
        filteredEnrollments = filteredEnrollments.filter(e => 
          new Date(e.created_at) >= startDate
        );
      }
    }

    // === LEARNER FILTERING ===
    let filteredLearners = [...learners];
    
    // Filter by grade
    if (filters.learnerGrade !== 'all') {
      filteredLearners = filteredLearners.filter(l => l.grade === filters.learnerGrade);
    }
    
    // Filter by school district
    if (filters.learnerSchoolDistrict !== 'all') {
      filteredLearners = filteredLearners.filter(l => l.district_name === filters.learnerSchoolDistrict);
    }
    
    // Filter by partner school
    if (filters.learnerPartnerSchool !== 'all') {
      filteredLearners = filteredLearners.filter(l => 
        l.partner_school === filters.learnerPartnerSchool || 
        l.partner_school_id === filters.learnerPartnerSchool
      );
    }
    
    // Store filtered learners list for display
    setFilteredLearnersList(filteredLearners);

    // Calculate stats
    const parentCount = users.filter(u => u.role === 'parent').length;
    const instructorCount = users.filter(u => u.role === 'instructor').length;
    const adminCount = users.filter(u => u.role === 'admin' || u.role === 'super_admin').length;
    
    const activeEnrollments = filteredEnrollments.filter(e => e.status === 'confirmed' || e.status === 'pending').length;
    const completedEnrollments = filteredEnrollments.filter(e => e.status === 'completed').length;
    
    const activeSections = filteredSections.filter(s => s.active).length;
    const activeCourses = coursesData.filter(c => c.active).length;
    
    const pendingApplications = applications.filter(a => a.status === 'pending').length;
    
    // Calculate revenue
    let totalRevenue = 0;
    filteredEnrollments.forEach(e => {
      const section = sections.find(s => s.id === e.section || s.id === e.section_id);
      if (section && (e.status === 'confirmed' || e.status === 'completed')) {
        totalRevenue += parseFloat(section.price || 0);
      }
    });

    // Get unique learners from filtered enrollments (when enrollment filters are active)
    const uniqueLearnerIds = [...new Set(filteredEnrollments.map(e => e.learner || e.learner_id))];
    const learnersFromEnrollments = learners.filter(l => uniqueLearnerIds.includes(l.id));
    
    // Determine learner count based on active filters
    const hasEnrollmentFilters = filters.courseId !== 'all' || filters.locationId !== 'all' || filters.instructorId !== 'all';
    const hasLearnerFilters = filters.learnerGrade !== 'all' || filters.learnerSchoolDistrict !== 'all' || filters.learnerPartnerSchool !== 'all';
    
    let finalLearnerCount;
    if (hasEnrollmentFilters && hasLearnerFilters) {
      // Both filters active - intersection
      const enrolledLearnerIds = new Set(uniqueLearnerIds);
      finalLearnerCount = filteredLearners.filter(l => enrolledLearnerIds.has(l.id)).length;
    } else if (hasEnrollmentFilters) {
      finalLearnerCount = learnersFromEnrollments.length;
    } else if (hasLearnerFilters) {
      finalLearnerCount = filteredLearners.length;
    } else {
      finalLearnerCount = learners.length;
    }

    // Calculate grade distribution
    const gradeDistribution = {};
    filteredLearners.forEach(l => {
      const grade = l.grade || 'Unknown';
      gradeDistribution[grade] = (gradeDistribution[grade] || 0) + 1;
    });

    // Calculate school distribution
    const schoolDistribution = {};
    filteredLearners.forEach(l => {
      const school = l.partner_school_name || l.school || 'No School';
      schoolDistribution[school] = (schoolDistribution[school] || 0) + 1;
    });

    setStats({
      users: {
        total: users.length,
        parents: parentCount,
        instructors: instructorCount,
        admins: adminCount,
      },
      learners: {
        total: finalLearnerCount,
        allTotal: learners.length,
        filteredTotal: filteredLearners.length,
        avgPointsBalance: filteredLearners.length > 0 
          ? Math.round(filteredLearners.reduce((sum, l) => sum + (l.points_balance || 0), 0) / filteredLearners.length)
          : 0,
        gradeDistribution,
        schoolDistribution,
        withPartnerSchool: filteredLearners.filter(l => l.partner_school || l.partner_school_id).length,
      },
      courses: {
        total: coursesData.length,
        active: activeCourses,
      },
      sections: {
        total: filteredSections.length,
        active: activeSections,
        totalCapacity: filteredSections.reduce((sum, s) => sum + (s.capacity || 0), 0),
      },
      enrollments: {
        total: filteredEnrollments.length,
        active: activeEnrollments,
        completed: completedEnrollments,
        cancelled: filteredEnrollments.filter(e => e.status === 'cancelled').length,
        waitlisted: filteredEnrollments.filter(e => e.status === 'waitlisted').length,
      },
      locations: {
        total: locationsData.length,
        active: locationsData.filter(l => l.active).length,
      },
      kits: {
        total: kits.length,
        lowStock: kits.filter(k => (k.stock_quantity || 0) < 5).length,
      },
      applications: {
        total: applications.length,
        pending: pendingApplications,
      },
      revenue: totalRevenue,
    });
  };

  const clearFilters = () => {
    setFilters({
      timeRange: 'all',
      courseId: 'all',
      locationId: 'all',
      instructorId: 'all',
      enrollmentStatus: 'all',
      dateFrom: '',
      dateTo: '',
      learnerGrade: 'all',
      learnerSchoolDistrict: 'all',
      learnerPartnerSchool: 'all',
    });
  };

  const clearLearnerFilters = () => {
    setFilters({
      ...filters,
      learnerGrade: 'all',
      learnerSchoolDistrict: 'all',
      learnerPartnerSchool: 'all',
    });
  };

  const learnerFilterCount = [
    filters.learnerGrade !== 'all',
    filters.learnerSchoolDistrict !== 'all',
    filters.learnerPartnerSchool !== 'all',
  ].filter(Boolean).length;

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => 
    value !== 'all' && value !== ''
  ).length;

  // Export functions
  const exportToCSV = (data, filename) => {
    const csvContent = data.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportSummaryReport = () => {
    if (!stats) return;
    
    const now = new Date().toISOString().split('T')[0];
    const data = [
      ['Ready Set STEM - Summary Report'],
      [`Generated: ${new Date().toLocaleString()}`],
      [''],
      ['=== KEY METRICS ==='],
      ['Metric', 'Value'],
      ['Total Revenue', `$${stats.revenue?.toLocaleString() || 0}`],
      ['Active Enrollments', stats.enrollments?.active || 0],
      ['Completed Enrollments', stats.enrollments?.completed || 0],
      ['Total Learners', stats.learners?.total || 0],
      ['Average Points Balance', stats.learners?.avgPointsBalance || 0],
      [''],
      ['=== USERS ==='],
      ['User Type', 'Count'],
      ['Parents', stats.users?.parents || 0],
      ['Instructors', stats.users?.instructors || 0],
      ['Admins', stats.users?.admins || 0],
      ['Total Users', stats.users?.total || 0],
      [''],
      ['=== COURSES & SECTIONS ==='],
      ['Metric', 'Value'],
      ['Active Courses', stats.courses?.active || 0],
      ['Total Courses', stats.courses?.total || 0],
      ['Active Sections', stats.sections?.active || 0],
      ['Total Sections', stats.sections?.total || 0],
      ['Total Capacity', stats.sections?.totalCapacity || 0],
      [''],
      ['=== ENROLLMENTS ==='],
      ['Status', 'Count'],
      ['Active', stats.enrollments?.active || 0],
      ['Completed', stats.enrollments?.completed || 0],
      ['Cancelled', stats.enrollments?.cancelled || 0],
      ['Waitlisted', stats.enrollments?.waitlisted || 0],
      ['Total', stats.enrollments?.total || 0],
      [''],
      ['=== OTHER ==='],
      ['Metric', 'Value'],
      ['Active Locations', stats.locations?.active || 0],
      ['Total STEM Kits', stats.kits?.total || 0],
      ['Low Stock Kits', stats.kits?.lowStock || 0],
      ['Pending Applications', stats.applications?.pending || 0],
    ];

    // Add active filters info
    if (activeFilterCount > 0) {
      data.push(['']);
      data.push(['=== ACTIVE FILTERS ===']);
      if (filters.timeRange !== 'all') data.push(['Time Range', filters.timeRange]);
      if (filters.courseId !== 'all') {
        const course = courses.find(c => c.id === filters.courseId);
        data.push(['Course', course?.title || filters.courseId]);
      }
      if (filters.locationId !== 'all') {
        const location = locations.find(l => l.id === filters.locationId);
        data.push(['Location', location?.name || filters.locationId]);
      }
      if (filters.instructorId !== 'all') {
        const instructor = instructors.find(i => i.id === filters.instructorId);
        data.push(['Instructor', instructor ? `${instructor.first_name} ${instructor.last_name}` : filters.instructorId]);
      }
      if (filters.enrollmentStatus !== 'all') data.push(['Enrollment Status', filters.enrollmentStatus]);
      if (filters.dateFrom) data.push(['From Date', filters.dateFrom]);
      if (filters.dateTo) data.push(['To Date', filters.dateTo]);
    }

    exportToCSV(data, `rsc_summary_report_${now}.csv`);
    toast.success('Summary report exported!');
  };

  const exportEnrollmentsReport = () => {
    if (!rawData.enrollments) return;

    const { enrollments, sections, learners } = rawData;
    const now = new Date().toISOString().split('T')[0];
    
    const data = [
      ['Learner Name', 'Course', 'Section', 'Location', 'Status', 'Enrolled Date', 'Price']
    ];

    enrollments.forEach(e => {
      const section = sections.find(s => s.id === e.section || s.id === e.section_id);
      const learner = learners.find(l => l.id === e.learner || l.id === e.learner_id);
      const course = courses.find(c => c.id === section?.course || c.id === section?.course_id);
      const location = locations.find(l => l.id === section?.location || l.id === section?.location_id);
      
      data.push([
        learner ? `${learner.first_name} ${learner.last_name}` : 'Unknown',
        course?.title || 'Unknown',
        section?.term || 'Unknown',
        location?.name || 'Online',
        e.status || 'Unknown',
        e.created_at ? new Date(e.created_at).toLocaleDateString() : 'Unknown',
        section?.price ? `$${section.price}` : '$0'
      ]);
    });

    exportToCSV(data, `rsc_enrollments_${now}.csv`);
    toast.success('Enrollments report exported!');
  };

  const exportUsersReport = () => {
    if (!rawData.users) return;

    const now = new Date().toISOString().split('T')[0];
    
    const data = [
      ['Email', 'First Name', 'Last Name', 'Role', 'Status', 'Created Date']
    ];

    rawData.users.forEach(u => {
      data.push([
        u.email || '',
        u.first_name || '',
        u.last_name || '',
        u.role || 'Unknown',
        u.is_active ? 'Active' : 'Inactive',
        u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Unknown'
      ]);
    });

    exportToCSV(data, `rsc_users_${now}.csv`);
    toast.success('Users report exported!');
  };

  const exportLearnersReport = () => {
    if (!rawData.learners) return;

    const now = new Date().toISOString().split('T')[0];
    
    const data = [
      ['First Name', 'Last Name', 'Grade', 'School', 'Points Balance', 'Created Date']
    ];

    rawData.learners.forEach(l => {
      data.push([
        l.first_name || '',
        l.last_name || '',
        l.grade || '',
        l.school || 'N/A',
        l.points_balance || 0,
        l.created_at ? new Date(l.created_at).toLocaleDateString() : 'Unknown'
      ]);
    });

    exportToCSV(data, `rsc_learners_${now}.csv`);
    toast.success('Learners report exported!');
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/10`}>
            <Icon className={`h-6 w-6 text-${color}`} />
          </div>
        </div>
        {trend && (
          <div className="mt-3 flex items-center gap-1 text-sm">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-green-500">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground">Overview of your Ready Set STEM operations</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant={showFilters ? "default" : "outline"} 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-white text-primary">{activeFilterCount}</Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="space-y-2">
                <p className="text-sm font-medium mb-3">Export Reports</p>
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={exportSummaryReport}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Summary Report
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={exportEnrollmentsReport}>
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Enrollments
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={exportUsersReport}>
                  <Users className="h-4 w-4 mr-2" />
                  Users
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={exportLearnersReport}>
                  <Users className="h-4 w-4 mr-2" />
                  Learners
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Time Range */}
              <div className="space-y-2">
                <Label>Time Range</Label>
                <Select value={filters.timeRange} onValueChange={(v) => setFilters({...filters, timeRange: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                    <SelectItem value="quarter">Last 3 Months</SelectItem>
                    <SelectItem value="year">Last Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Course Filter */}
              <div className="space-y-2">
                <Label>Course</Label>
                <Select value={filters.courseId} onValueChange={(v) => setFilters({...filters, courseId: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All courses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Courses</SelectItem>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location Filter */}
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={filters.locationId} onValueChange={(v) => setFilters({...filters, locationId: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Instructor Filter */}
              <div className="space-y-2">
                <Label>Instructor</Label>
                <Select value={filters.instructorId} onValueChange={(v) => setFilters({...filters, instructorId: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All instructors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Instructors</SelectItem>
                    {instructors.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>
                        {inst.first_name} {inst.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Enrollment Status */}
              <div className="space-y-2">
                <Label>Enrollment Status</Label>
                <Select value={filters.enrollmentStatus} onValueChange={(v) => setFilters({...filters, enrollmentStatus: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="waitlisted">Waitlisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <Label>From Date</Label>
                <Input 
                  type="date" 
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                />
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input 
                  type="date" 
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                />
              </div>

              {/* Clear Filters */}
              <div className="space-y-2 flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Clear All Filters
                </Button>
              </div>
            </div>

            {/* Active Filters Summary */}
            {activeFilterCount > 0 && (
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {filters.timeRange !== 'all' && (
                  <Badge variant="secondary">
                    Time: {filters.timeRange}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, timeRange: 'all'})} />
                  </Badge>
                )}
                {filters.courseId !== 'all' && (
                  <Badge variant="secondary">
                    Course: {courses.find(c => c.id === filters.courseId)?.title?.substring(0, 15)}...
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, courseId: 'all'})} />
                  </Badge>
                )}
                {filters.locationId !== 'all' && (
                  <Badge variant="secondary">
                    Location: {locations.find(l => l.id === filters.locationId)?.name}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, locationId: 'all'})} />
                  </Badge>
                )}
                {filters.instructorId !== 'all' && (
                  <Badge variant="secondary">
                    Instructor: {instructors.find(i => i.id === filters.instructorId)?.first_name}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, instructorId: 'all'})} />
                  </Badge>
                )}
                {filters.enrollmentStatus !== 'all' && (
                  <Badge variant="secondary">
                    Status: {filters.enrollmentStatus}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, enrollmentStatus: 'all'})} />
                  </Badge>
                )}
                {filters.dateFrom && (
                  <Badge variant="secondary">
                    From: {filters.dateFrom}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, dateFrom: ''})} />
                  </Badge>
                )}
                {filters.dateTo && (
                  <Badge variant="secondary">
                    To: {filters.dateTo}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, dateTo: ''})} />
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`$${stats?.revenue?.toLocaleString() || 0}`}
          subtitle="From confirmed enrollments"
          icon={DollarSign}
          color="green-500"
        />
        <StatCard
          title="Active Enrollments"
          value={stats?.enrollments?.active || 0}
          subtitle={`${stats?.enrollments?.completed || 0} completed`}
          icon={GraduationCap}
          color="primary"
        />
        <StatCard
          title="Total Learners"
          value={stats?.learners?.total || 0}
          subtitle={learnerFilterCount > 0 ? `Filtered from ${stats?.learners?.allTotal || 0}` : `Avg ${stats?.learners?.avgPointsBalance || 0} points`}
          icon={Users}
          color="blue-500"
        />
        <StatCard
          title="Active Sections"
          value={stats?.sections?.active || 0}
          subtitle={`${stats?.sections?.totalCapacity || 0} total capacity`}
          icon={Calendar}
          color="amber-500"
        />
      </div>

      {/* Learner Filter Section */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Learner Filter & Analysis
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant={showLearnerFilters ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowLearnerFilters(!showLearnerFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter Learners
                {learnerFilterCount > 0 && (
                  <Badge className="ml-2 bg-white text-primary">{learnerFilterCount}</Badge>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Learner Filters */}
          {showLearnerFilters && (
            <div className="mb-6 p-4 bg-white rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Grade Filter */}
                <div className="space-y-2">
                  <Label>Grade Level</Label>
                  <Select value={filters.learnerGrade} onValueChange={(v) => setFilters({...filters, learnerGrade: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All grades" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Grades</SelectItem>
                      <SelectItem value="K">Kindergarten</SelectItem>
                      <SelectItem value="1">Grade 1</SelectItem>
                      <SelectItem value="2">Grade 2</SelectItem>
                      <SelectItem value="3">Grade 3</SelectItem>
                      <SelectItem value="4">Grade 4</SelectItem>
                      <SelectItem value="5">Grade 5</SelectItem>
                      <SelectItem value="6">Grade 6</SelectItem>
                      <SelectItem value="7">Grade 7</SelectItem>
                      <SelectItem value="8">Grade 8</SelectItem>
                      <SelectItem value="9">Grade 9</SelectItem>
                      <SelectItem value="10">Grade 10</SelectItem>
                      <SelectItem value="11">Grade 11</SelectItem>
                      <SelectItem value="12">Grade 12</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* School District Filter */}
                <div className="space-y-2">
                  <Label>School District</Label>
                  <Select value={filters.learnerSchoolDistrict} onValueChange={(v) => setFilters({...filters, learnerSchoolDistrict: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All districts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Districts</SelectItem>
                      {schoolDistricts.map(district => (
                        <SelectItem key={district} value={district}>{district}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Partner School Filter */}
                <div className="space-y-2">
                  <Label>Partner School</Label>
                  <Select value={filters.learnerPartnerSchool} onValueChange={(v) => setFilters({...filters, learnerPartnerSchool: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="All schools" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Partner Schools</SelectItem>
                      {partnerSchools.map(school => (
                        <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {learnerFilterCount > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground">Active filters:</span>
                    {filters.learnerGrade !== 'all' && (
                      <Badge variant="secondary">
                        Grade: {filters.learnerGrade === 'K' ? 'Kindergarten' : filters.learnerGrade}
                        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, learnerGrade: 'all'})} />
                      </Badge>
                    )}
                    {filters.learnerSchoolDistrict !== 'all' && (
                      <Badge variant="secondary">
                        District: {filters.learnerSchoolDistrict}
                        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, learnerSchoolDistrict: 'all'})} />
                      </Badge>
                    )}
                    {filters.learnerPartnerSchool !== 'all' && (
                      <Badge variant="secondary">
                        School: {partnerSchools.find(s => s.id === filters.learnerPartnerSchool)?.name}
                        <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, learnerPartnerSchool: 'all'})} />
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearLearnerFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Learner Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-blue-600">{stats?.learners?.total || 0}</p>
              <p className="text-sm text-muted-foreground">
                {learnerFilterCount > 0 ? 'Filtered Learners' : 'Total Learners'}
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-green-600">{stats?.learners?.withPartnerSchool || 0}</p>
              <p className="text-sm text-muted-foreground">With Partner School</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-amber-600">{stats?.learners?.avgPointsBalance || 0}</p>
              <p className="text-sm text-muted-foreground">Avg Points Balance</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-purple-600">{Object.keys(stats?.learners?.gradeDistribution || {}).length}</p>
              <p className="text-sm text-muted-foreground">Grade Levels</p>
            </div>
          </div>

          {/* Grade Distribution */}
          {stats?.learners?.gradeDistribution && Object.keys(stats.learners.gradeDistribution).length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium mb-3">Grade Distribution</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.learners.gradeDistribution)
                  .sort(([a], [b]) => {
                    if (a === 'K') return -1;
                    if (b === 'K') return 1;
                    return parseInt(a) - parseInt(b);
                  })
                  .map(([grade, count]) => (
                    <Badge 
                      key={grade} 
                      variant="outline" 
                      className="px-3 py-1 cursor-pointer hover:bg-primary hover:text-white transition-colors"
                      onClick={() => setFilters({...filters, learnerGrade: grade})}
                    >
                      {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}: {count}
                    </Badge>
                  ))
                }
              </div>
            </div>
          )}

          {/* School Distribution */}
          {stats?.learners?.schoolDistribution && Object.keys(stats.learners.schoolDistribution).length > 0 && (
            <div>
              <h4 className="font-medium mb-3">School Distribution</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.learners.schoolDistribution)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([school, count]) => (
                    <Badge 
                      key={school} 
                      variant="secondary" 
                      className="px-3 py-1"
                    >
                      {school}: {count}
                    </Badge>
                  ))
                }
                {Object.keys(stats.learners.schoolDistribution).length > 10 && (
                  <Badge variant="outline" className="px-3 py-1">
                    +{Object.keys(stats.learners.schoolDistribution).length - 10} more
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Users Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Users Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Parents</span>
                <Badge variant="secondary">{stats?.users?.parents || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Instructors</span>
                <Badge variant="secondary">{stats?.users?.instructors || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Admins</span>
                <Badge variant="secondary">{stats?.users?.admins || 0}</Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Total Users</span>
                <Badge>{stats?.users?.total || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Courses & Sections */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Courses & Sections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Active Courses</span>
                <Badge variant="secondary">{stats?.courses?.active || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Courses</span>
                <Badge variant="outline">{stats?.courses?.total || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Active Sections</span>
                <Badge variant="secondary">{stats?.sections?.active || 0}</Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Total Sections</span>
                <Badge>{stats?.sections?.total || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Enrollment Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Enrollment Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Active</span>
                <Badge className="bg-green-100 text-green-700">{stats?.enrollments?.active || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Completed</span>
                <Badge className="bg-blue-100 text-blue-700">{stats?.enrollments?.completed || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Cancelled</span>
                <Badge className="bg-red-100 text-red-700">{stats?.enrollments?.cancelled || 0}</Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Total Enrollments</span>
                <Badge>{stats?.enrollments?.total || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Locations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Active Locations</span>
                <Badge variant="secondary">{stats?.locations?.active || 0}</Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Total Locations</span>
                <Badge>{stats?.locations?.total || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* STEM Kits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              STEM Kits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Kits</span>
                <Badge variant="secondary">{stats?.kits?.total || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Low Stock</span>
                <Badge className={stats?.kits?.lowStock > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                  {stats?.kits?.lowStock || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Applications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Pending Review</span>
                <Badge className={stats?.applications?.pending > 0 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>
                  {stats?.applications?.pending || 0}
                </Badge>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Total Applications</span>
                <Badge>{stats?.applications?.total || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/users'}>
              <Users className="h-4 w-4 mr-2" />
              Manage Users
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/courses'}>
              <BookOpen className="h-4 w-4 mr-2" />
              Manage Courses
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/kits'}>
              <Package className="h-4 w-4 mr-2" />
              Manage Kits
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/applications'}>
              <UserCheck className="h-4 w-4 mr-2" />
              Review Applications
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
